<template>
  <div class="training-plan-kanban">
    <div class="kanban-header d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0 font-weight-bold">Training Plan</h5>
    </div>

    <div class="kanban-board">
      <div
        v-for="column in columns"
        :key="column.year"
        class="kanban-column"
      >
        <!-- Column Header -->
        <div class="kanban-column-header d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <span class="year-badge mr-2">{{ column.year }}</span>
            <span class="badge badge-secondary">{{ column.items.length }} training</span>
          </div>
        </div>

        <draggable
          :list="column.items"
          group="training"
          ghost-class="card-ghost"
          chosen-class="card-chosen"
          drag-class="card-drag"
          animation="200"
          class="kanban-card-list"
          :empty-insert-threshold="40"
          :filter="'.kanban-empty-drop'"
          :move="onMove"
          @end="onDragEnd"
        >
          <div
            v-for="item in column.items"
            :key="item.id"
            class="kanban-card"
            :class="{ 'kanban-card--mandatory': isMandatory(item) }"
          >
            <div class="kanban-card-inner">
              <div class="card-content-wrap">
                <div class="d-flex align-items-start justify-content-between">
                  <div class="flex-grow-1">
                    <p class="card-title-text mb-1">{{ item.title }}</p>
                    <span v-if="isMandatory(item)" class="mandatory-label">
                      <i class="fe fe-check-circle"></i> Mandatory
                    </span>
                    <span v-else class="dev-label text-info">
                      <i class="fe fe-settings"></i> Development
                    </span>
                  </div>
                  <i
                    class="fe fe-menu text-muted ml-2 drag-handle"
                    style="cursor: grab; margin-top: 2px"
                  ></i>
                </div>
              </div>
              <div class="card-actions-footer mt-2">
                <div class="card-actions">
                  <button
                    class="btn btn-sm btn-outline-primary p-1"
                    style="width: 28px; height: 28px; display: flex; align-items: center; justify-content: center"
                    @click.stop="openDetail(item)"
                    title="View detail"
                  >
                    <i class="fe fe-eye" style="font-size: 14px"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State inside draggable -->
          <div
            v-if="column.items.length === 0"
            slot="footer"
            class="kanban-empty-drop"
          >
            <i class="fe fe-inbox"></i>
            <p class="mb-0">Drop training di sini</p>
          </div>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

function mapItemsToYearColumns(years, items = []) {
  const firstYear = years[0];
  const grouped = years.reduce((acc, year) => {
    acc[year] = [];
    return acc;
  }, {});

  (items || []).forEach((item) => {
    const itemYear = Number(item.year);
    const targetYear = grouped[itemYear] ? itemYear : firstYear;

    grouped[targetYear].push({ ...item, mandatory: isMandatoryTraining(item), year: targetYear });
  });

  return years.map((year) => ({
    year,
    items: grouped[year],
  }));
}

function isMandatoryTraining(item = {}) {
  const directValue =
    item.mandatory ??
    item.is_mandatory ??
    item.training_mapping?.is_mandatory ??
    item.training_mapping?.mandatory ??
    item.training?.training_mappings?.[0]?.mandatory ??
    item.training?.training_mappings?.[0]?.is_mandatory;

  if (typeof directValue === "boolean") return directValue;
  if (typeof directValue === "number") return directValue === 1;
  if (typeof directValue === "string") {
    const normalized = directValue.trim().toLowerCase();
    if (["true", "1", "yes", "y", "mandatory"].includes(normalized)) return true;
    if (["false", "0", "no", "n", "development", "gap", "gap_competencies"].includes(normalized)) return false;
  }

  return String(item.remarks || item.classification || "")
    .trim()
    .toLowerCase()
    .includes("mandatory");
}

export default {
  name: "TrainingPlanKanban",
  components: { draggable },
  props: {
    externalItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

    return {
      columns: mapItemsToYearColumns(years, this.externalItems || []),
      nextId: 100,
    };
  },
  watch: {
    externalItems: {
      handler(val) {
        const years = this.columns.map((column) => column.year);
        this.columns = mapItemsToYearColumns(years, val || []);
      },
      deep: true,
    },
  },
  mounted() {
    this.syncItemYears();
    this.$emit("plan-updated", this.columns);
  },
  methods: {
    getCategoryBadge(category) {
      const map = {
        Structured: "badge-primary",
        Social: "badge-success",
        Experiential: "badge-warning",
      };
      return map[category] || "badge-secondary";
    },
    onDragEnd(event) {
      this.syncItemYears();
      this.$emit("plan-updated", this.columns);
    },
    onMove(evt) {
      return !evt.draggedContext.element.locked;
    },
    syncItemYears() {
      this.columns.forEach((column) => {
        column.items.forEach((item) => {
          item.year = column.year;
        });
      });
    },
    addCard(column) {
      const title = prompt(`Tambah training untuk tahun ${column.year}:`);
      if (title && title.trim()) {
        column.items.push({
          id: this.nextId++,
          title: title.trim(),
          category: "Structured",
          provider: "",
          duration: "",
          year: column.year,
        });
      }
    },
    openDetail(item) {
      this.$emit("open-training-detail", item);
    },
    isMandatory(item) {
      return isMandatoryTraining(item);
    },
  },
};
</script>

<style scoped>
.training-plan-kanban {
  padding: 1rem 0;
}

/* Board: vertical stack */
.kanban-board {
  display: flex;
  flex-direction: row;
  gap: 1rem;
}

/* Each year column */
.kanban-column {
  flex: 1;
  min-width: 0;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.kanban-column-header {
  padding: 0.75rem 1rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.year-badge {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
}

/* Card list inside each column — horizontal scroll for cards */
.kanban-card-list {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  min-height: 100px;
  overflow-x: auto;
  align-items: flex-start;
}

.kanban-card {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: grab;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.kanban-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.kanban-card-inner {
  padding: 0.75rem;
}

.card-title-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #343a40;
  line-height: 1.3;
  margin: 0;
}

.card-content-wrap,
.card-content {
  flex: 1;
}

.card-actions-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.card-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

/* Empty drop zone */
.kanban-empty-drop {
  min-height: 80px;
  width: 100%;
  border: 2px dashed #ced4da;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.8rem;
  gap: 4px;
}

/* Drag states */
.card-ghost {
  opacity: 0.4;
  background: #cfe2ff;
  border: 2px dashed #0d6efd;
}

.card-chosen {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.card-drag {
  transform: rotate(2deg);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
}

.drag-handle:active {
  cursor: grabbing;
}

.kanban-card--mandatory {
  border-color: #f7d98b;
  background: #fffaf0;
}

.kanban-card--mandatory:hover {
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.18);
}

.mandatory-label {
  font-size: 0.72rem;
  color: #f59e0b;
  font-weight: 600;
}

.dev-label {
  font-size: 0.72rem;
  font-weight: 600;
}

/* Scrollbar styling */
.kanban-card-list::-webkit-scrollbar {
  height: 4px;
}
.kanban-card-list::-webkit-scrollbar-track {
  background: transparent;
}
.kanban-card-list::-webkit-scrollbar-thumb {
  background: #ced4da;
  border-radius: 4px;
}
</style>
